import { useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useAuthStore } from "@/lib/stores/auth";
import { useBook, useStartBookProgress } from "@/lib/api/books";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { ImportanceBadge } from "@/components/book/importance-badge";
import {
  Loader2Icon,
  ArrowLeftIcon,
  ArrowRightIcon,
  CheckIcon,
  CircleDotIcon,
  CircleIcon,
  LockIcon,
  ChevronRightIcon,
} from "lucide-react";
import { t } from "@/lib/typography";
import { BookIntroVideo } from "@/components/book/book-intro-video";
import type { BookDetail, ChapterDetail } from "@/types/book";

function StatusIcon({ chapter }: { chapter: ChapterDetail }) {
  if (chapter.is_completed) {
    return <CheckIcon className="size-4 text-green-600 dark:text-green-400 shrink-0" />;
  }
  if (chapter.assessment_id != null) {
    return <CircleDotIcon className="size-4 text-primary shrink-0" />;
  }
  return <CircleIcon className="size-4 text-muted-foreground/50 shrink-0" />;
}

function StartingAssessmentRow({ book }: { book: BookDetail }) {
  if (!book.starting_assessment_id) return null;

  const isCompleted = book.progress?.completed_starting_assessment_at != null;

  return (
    <div className="flex items-center justify-between py-3">
      <div className="flex items-center gap-3">
        {isCompleted ? (
          <CheckIcon className="size-4 text-green-600 dark:text-green-400" />
        ) : (
          <CircleDotIcon className="size-4 text-primary" />
        )}
        <span className="font-medium">Starting Assessment</span>
        {isCompleted && <span className={t.caption}>Completed</span>}
      </div>
      <Link to={`/assessments/${book.starting_assessment_id}/take`}>
        <Button variant="outline" size="sm" className="gap-1.5">
          {isCompleted ? "Review" : "Take Assessment"}
          <ArrowRightIcon className="size-3.5" />
        </Button>
      </Link>
    </div>
  );
}

function FinishAssessmentRow({ book }: { book: BookDetail }) {
  const isCompleted = book.progress?.completed_finish_assessment_at != null;
  const isUnlocked = book.finish_assessment_id != null;

  return (
    <div className="flex items-center justify-between py-3">
      <div className="flex items-center gap-3">
        {isCompleted ? (
          <CheckIcon className="size-4 text-green-600 dark:text-green-400" />
        ) : isUnlocked ? (
          <CircleDotIcon className="size-4 text-primary" />
        ) : (
          <CircleIcon className="size-4 text-muted-foreground/50" />
        )}
        <span className="font-medium">Finish Assessment</span>
        {!isUnlocked && (
          <span className="flex items-center gap-1.5 text-muted-foreground">
            <LockIcon className="size-3" />
            Complete all chapters to unlock
          </span>
        )}
        {isCompleted && <span className={t.caption}>Completed</span>}
      </div>
      {isUnlocked && (
        <Link to={`/assessments/${book.finish_assessment_id}/take`}>
          <Button variant="outline" size="sm" className="gap-1.5">
            {isCompleted ? "Review" : "Take Assessment"}
            <ArrowRightIcon className="size-3.5" />
          </Button>
        </Link>
      )}
    </div>
  );
}

function getCompletedCount(book: BookDetail): number {
  return book.progress?.last_completed_chapter_order ?? 0;
}

export function BookProgress() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const bookId = Number(id);

  const { isAuthenticated, isLoading: authLoading } = useAuthStore();
  const { data: book, isLoading: bookLoading } = useBook(bookId);
  const startProgress = useStartBookProgress();

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate("/login");
    }
  }, [authLoading, isAuthenticated, navigate]);

  const handleStart = async () => {
    try {
      await startProgress.mutateAsync(bookId);
    } catch {
      // 409 = already started, ignore
    }
  };

  if (authLoading || bookLoading) {
    return (
      <div className="flex min-h-[calc(100vh-8rem)] items-center justify-center">
        <Loader2Icon className="size-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!book) {
    return (
      <div className="space-y-4">
        <Link
          to="/dashboard"
          className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeftIcon className="size-4" />
          Back to Dashboard
        </Link>
        <Card>
          <CardContent className="py-8 text-center">
            <p className="text-muted-foreground">Book not found.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const progress = book.progress;
  const completedCount = getCompletedCount(book);
  const progressPercent =
    book.total_chapters > 0
      ? (completedCount / book.total_chapters) * 100
      : 0;
  const isFullyCompleted = progress?.completed_finish_assessment_at != null;

  return (
    <div className="space-y-6">
      {/* Back link */}
      <Link
        to="/dashboard"
        className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeftIcon className="size-4" />
        Back to Dashboard
      </Link>

      {/* Book header */}
      <div className="space-y-2">
        <h1 className={`flex items-center gap-2 ${t.pageTitle}`}>
          {isFullyCompleted && (
            <CheckIcon className="size-6 text-green-600 dark:text-green-400" />
          )}
          {book.title}
        </h1>
        <p className={t.meta}>{book.author}</p>

        {progress && (
          <div className="space-y-1.5 pt-2">
            <Progress value={progressPercent} />
            <p className={t.meta}>
              {completedCount} / {book.total_chapters} chapters
              {isFullyCompleted &&
                progress.completed_finish_assessment_at &&
                ` · Completed ${new Date(progress.completed_finish_assessment_at).toLocaleDateString()}`}
            </p>
          </div>
        )}
      </div>

      {/* Book intro video */}
      {book.video_intro && (
        <BookIntroVideo src={book.video_intro} title={book.title} />
      )}

      {/* Not started */}
      {!progress && (
        <div className="space-y-6">
          {book.description && <p>{book.description}</p>}
          <Button
            onClick={handleStart}
            disabled={startProgress.isPending}
            className="gap-1.5"
          >
            {startProgress.isPending ? (
              <Loader2Icon className="size-4 animate-spin" />
            ) : (
              <>
                Start Reading This Book
                <ArrowRightIcon className="size-4" />
              </>
            )}
          </Button>
        </div>
      )}

      {/* Chapter list */}
      {progress && (
        <>
          <div className="border-t" />
          <StartingAssessmentRow book={book} />
          <div className="border-t" />

          <div className="space-y-1">
            {book.chapters.map((ch) => (
              <Link
                key={ch.id}
                to={`/books/${book.id}/chapters/${ch.order}`}
                className="flex items-center gap-3 rounded-lg px-4 py-3 transition-colors hover:bg-accent/50"
              >
                <StatusIcon chapter={ch} />
                <span className="min-w-0 flex-1 truncate">
                  Ch {ch.order} · {ch.title}
                </span>
                <ImportanceBadge importance={ch.importance} />
                {ch.time_estimate && (
                  <span className="shrink-0">{ch.time_estimate}</span>
                )}
                {!ch.is_completed && ch.assessment_id == null && (
                  <LockIcon className="size-3.5 shrink-0 text-muted-foreground/50" />
                )}
                <ChevronRightIcon className="size-4 shrink-0 text-muted-foreground/50" />
              </Link>
            ))}
          </div>

          <div className="border-t" />
          <FinishAssessmentRow book={book} />
        </>
      )}
    </div>
  );
}
